import React, { useState, useEffect } from "react";
import "./MealCard.css";
import {
    getMeal,
    getMealPreference,
    getIngredient,
    getProduct,
} from "../../../api/OtterApi";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import NutritionSquare from "./NutritionSquare";

const mealCache = new Map();
const mealPrefCache = new Map();

const MealCard = ({
    plannedMealPref,
    onDeletePlannedMealPref,
    isEditable,
    showNutrition,
    nutrition,
    ingredientNutrition,
    onDragStart,
    onCardDragOver,
    onCardDrop,
    onEditPlannedMealPref,
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [meal, setMeal] = useState(() =>
        mealCache.get(plannedMealPref?.mealpreference_id),
    );
    const history = useHistory();
    const truncateLength = 30;
    const [pendingDeletion, setPendingDeletion] = useState(false);
    const [mealPreference, setMealPreference] = useState(() =>
        mealPrefCache.get(plannedMealPref?.mealpreference_id),
    );
    const [isLoading, setIsLoading] = useState(
        !mealCache.has(plannedMealPref?.mealpreference_id),
    );
    const [missingNutrientsCount, setMissingNutrientsCount] = useState(0);
    const [ingredientMap, setIngredientMap] = useState({});
    const [productMap, setProductMap] = useState({});
    const [showActionButtons, setShowActionButtons] = useState(false);

    const formatMacro = (value) => {
        if (typeof value !== "number" || Number.isNaN(value)) {
            return "n/a";
        }
        return value.toFixed(1);
    };

    const getIngredientMacroRows = () => {
        if (
            Array.isArray(ingredientNutrition) &&
            ingredientNutrition.length > 0
        ) {
            return ingredientNutrition.map((ingredient, index) => {
                const ingredientName =
                    ingredient?.ingredient_name || "Ingredient";
                const ingredientMacro = ingredient?.nutrition;

                return (
                    <div
                        key={`${ingredient?.ingredient_id || ingredientName}-${index}`}
                        className="meal-card-ingredient-macro-row"
                    >
                        <div className="meal-card-ingredient-macro-name">
                            {ingredientName}
                        </div>
                        <div className="meal-card-ingredient-macro-values">
                            <span>
                                Cal: {formatMacro(ingredientMacro?.calories)}
                            </span>
                            <span>
                                C: {formatMacro(ingredientMacro?.carbohydrates)}
                            </span>
                            <span>F: {formatMacro(ingredientMacro?.fats)}</span>
                            <span>
                                P: {formatMacro(ingredientMacro?.proteins)}
                            </span>
                        </div>
                    </div>
                );
            });
        }

        const bundlePrefs = mealPreference?.bundle_prefs || [];
        if (bundlePrefs.length === 0) {
            return null;
        }

        return bundlePrefs.map((bundlePref) => {
            const ingredient = ingredientMap[bundlePref.ingredient_id];
            const ingredientName = ingredient?.name || "Loading ingredient...";
            const productMapping = {
                ...(mealPreference?.product_mapping_prioritized || {}),
                ...(mealPreference?.product_mapping_user || {}),
            };
            const mappedProductId = productMapping[bundlePref.ingredient_id];
            const mappedProductNutrition = mappedProductId
                ? productMap[mappedProductId]?.nutrition
                : undefined;
            const ingredientNutrition =
                bundlePref?.nutrition ||
                ingredient?.nutrition ||
                mappedProductNutrition;

            return (
                <div
                    key={bundlePref.id}
                    className="meal-card-ingredient-macro-row"
                >
                    <div className="meal-card-ingredient-macro-name">
                        {ingredientName}
                    </div>
                    <div className="meal-card-ingredient-macro-values">
                        <span>
                            Cal: {formatMacro(ingredientNutrition?.calories)}
                        </span>
                        <span>
                            C: {formatMacro(ingredientNutrition?.carbohydrates)}
                        </span>
                        <span>F: {formatMacro(ingredientNutrition?.fats)}</span>
                        <span>
                            P: {formatMacro(ingredientNutrition?.proteins)}
                        </span>
                    </div>
                </div>
            );
        });
    };

    useEffect(() => {
        const initializeMeal = async () => {
            setIsLoading(true);
            const token = await getAccessTokenSilently();

            const getMealPrefResponse = await getMealPreference(
                token,
                plannedMealPref.mealpreference_id,
            );
            if (getMealPrefResponse.status === 200) {
                const mealPref = getMealPrefResponse.data.mealpreference;
                mealPrefCache.set(plannedMealPref.mealpreference_id, mealPref);
                setMealPreference(mealPref);
                console.log("Retrieved meal preference:", mealPref);
                const nutritionTotal = Number(mealPref?.nutrition_total);
                const nutritionResolved = Number(mealPref?.nutrition_resolved);
                if (
                    Number.isFinite(nutritionTotal) &&
                    Number.isFinite(nutritionResolved)
                ) {
                    setMissingNutrientsCount(
                        Math.max(nutritionTotal - nutritionResolved, 0),
                    );
                }
                const getMealResponse = await getMeal(token, mealPref.meal_id);
                if (getMealResponse.status === 200) {
                    const retrievedMeal = getMealResponse.data.meal;
                    mealCache.set(
                        plannedMealPref.mealpreference_id,
                        retrievedMeal,
                    );
                    setMeal(retrievedMeal);
                }

                const ingredientIds = [
                    ...new Set(
                        (mealPref?.bundle_prefs || []).map(
                            (bundlePref) => bundlePref.ingredient_id,
                        ),
                    ),
                ];
                if (ingredientIds.length > 0) {
                    const ingredientResponses = await Promise.all(
                        ingredientIds.map((ingredientId) =>
                            getIngredient(token, ingredientId),
                        ),
                    );
                    const newIngredientMap = ingredientResponses.reduce(
                        (accumulator, response) => {
                            if (response.status === 200) {
                                const ingredient = response.data.ingredient;
                                accumulator[ingredient.id] = ingredient;
                            }
                            return accumulator;
                        },
                        {},
                    );
                    setIngredientMap(newIngredientMap);
                } else {
                    setIngredientMap({});
                }

                const mappedProductIds = [
                    ...new Set([
                        ...Object.values(
                            mealPref?.product_mapping_prioritized || {},
                        ),
                        ...Object.values(mealPref?.product_mapping_user || {}),
                    ]),
                ].filter(Boolean);

                if (mappedProductIds.length > 0) {
                    const productResponses = await Promise.all(
                        mappedProductIds.map((productId) =>
                            getProduct(token, productId),
                        ),
                    );
                    const newProductMap = productResponses.reduce(
                        (accumulator, response) => {
                            if (response.status === 200) {
                                const product = response.data.product;
                                accumulator[product.id] = product;
                            }
                            return accumulator;
                        },
                        {},
                    );
                    setProductMap(newProductMap);
                } else {
                    setProductMap({});
                }
            }
            setIsLoading(false);
        };
        if (plannedMealPref) {
            initializeMeal();
        }
    }, [plannedMealPref]);

    const navigateToMealDetails = (id) => {
        history.push(`/meal/${id}`);
    };

    const getCardElements = () => {
        meal.image_url = `https://picsum.photos/50/50?blur=10&random=${
            Math.floor(Math.random() * 1000) + 1
        }`;
        const resolvedMealNutrition = nutrition || mealPreference?.nutrition;
        const unresolvedIngredientsCount = Array.isArray(ingredientNutrition)
            ? ingredientNutrition.filter((ingredient) => {
                  const ingredientMacro = ingredient?.nutrition;
                  return ![
                      ingredientMacro?.calories,
                      ingredientMacro?.carbohydrates,
                      ingredientMacro?.fats,
                      ingredientMacro?.proteins,
                  ].some(
                      (macroValue) =>
                          typeof macroValue === "number" &&
                          !Number.isNaN(macroValue),
                  );
              }).length
            : missingNutrientsCount;

        return (
            <div
                className="meal-card-main-content"
                style={{
                    marginTop: "0px",
                }}
            >
                <div className="meal-card-container">
                    {/*
                    <img
                        src={meal?.image_url || apple}
                        alt="Default meal"
                        className="plannedmeal-card-image"
                    />
                    */}
                    <div className="meal-card-details">
                        <div
                            className="mealplan-detail-meal-name"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigateToMealDetails(meal?.id);
                            }}
                        >
                            {meal?.name?.substring(0, truncateLength)}
                            {meal?.name?.length > truncateLength ? "..." : ""}
                        </div>
                        <div className="mealplan-detail-meal-preference-name">
                            {mealPreference?.name?.substring(0, truncateLength)}
                            {mealPreference?.name?.length > truncateLength
                                ? "..."
                                : ""}
                        </div>
                    </div>
                </div>
                {showNutrition && (
                    <>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "4px",
                                marginTop: "5px",
                            }}
                        >
                            <NutritionSquare
                                name={"Cals"}
                                value={formatMacro(
                                    resolvedMealNutrition?.calories,
                                )}
                                color={"200, 200, 200"}
                            />
                            <NutritionSquare
                                name={"Carbs"}
                                value={formatMacro(
                                    resolvedMealNutrition?.carbohydrates,
                                )}
                                color={"248, 214, 137"}
                            />
                            <NutritionSquare
                                name={"Fats"}
                                value={formatMacro(resolvedMealNutrition?.fats)}
                                color={"136, 175, 248"}
                            />
                            <NutritionSquare
                                name={"Prot"}
                                value={formatMacro(
                                    resolvedMealNutrition?.proteins,
                                )}
                                color={"241, 161, 146"}
                            />
                        </div>
                        {/* <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                marginTop: "5px",
                            }}
                        >
                            {unresolvedIngredientsCount} ingredients without
                            macronutrient information
                        </div>
                        <div className="meal-card-ingredient-macro-card">
                            <div className="meal-card-ingredient-macro-header">
                                Ingredient Macro Breakdown
                            </div>
                            {getIngredientMacroRows()}
                        </div> */}
                    </>
                )}
            </div>
        );
    };

    return (
        <div
            className={`mealplan-detail-meal-card ${
                isEditable ? "mealplan-detail-meal-card-editable" : ""
            } ${
                showActionButtons
                    ? "mealplan-detail-meal-card-actions-visible"
                    : ""
            }`}
            draggable={isEditable}
            onDragOver={onCardDragOver}
            onDrop={onCardDrop}
            onDragStart={(e) => {
                if (!isEditable) return;
                setShowActionButtons(false);
                e.dataTransfer.setData(
                    "application/json",
                    JSON.stringify({
                        id: plannedMealPref.id,
                        mealpreference_id: plannedMealPref.mealpreference_id,
                        day: plannedMealPref.day,
                        time: plannedMealPref.time,
                    }),
                );
                e.dataTransfer.effectAllowed = "move";
                if (onDragStart) onDragStart();
            }}
            onClick={() => {
                if (isEditable) {
                    setShowActionButtons((current) => !current);
                }
            }}
            onMouseLeave={() => setShowActionButtons(false)}
        >
            {meal ? (
                <div className="plannedmeal-card-actions-overlay">
                    <div className="plannedmeal-card-action-row">
                        <button
                            className="plannedmeal-card-action-button plannedmeal-card-action-button-primary"
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigateToMealDetails(meal?.id);
                            }}
                        >
                            Go to Meal
                        </button>
                    </div>
                    {isEditable ? (
                        <>
                            <div className="plannedmeal-card-action-row">
                                <button
                                    className="plannedmeal-card-action-button"
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (onEditPlannedMealPref) onEditPlannedMealPref();
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    className="plannedmeal-card-action-button plannedmeal-card-action-button-danger"
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setPendingDeletion(true);
                                        onDeletePlannedMealPref(
                                            plannedMealPref.id,
                                            setPendingDeletion,
                                        );
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                            <span className="plannedmeal-card-drag-hint">Or drag and drop</span>
                        </>
                    ) : null}
                </div>
            ) : null}
            {meal
                ? pendingDeletion
                    ? "Deleting..."
                    : getCardElements()
                : "Loading..."}
        </div>
    );
};

export default MealCard;
